<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Link Supplier Invoice to Invoice
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new
                                                supplier invoice.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="mt-6 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Supplier
                                                        Information</h3>
                                                </div>
                                                <div class="col-span-2">
                                                    <label for="invoice"
                                                           class="block text-sm text-gray-900 required">
                                                        Related Invoice
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="invoice" v-model="form.invoice_id"
                                                                :class="{'border-red-500': form.errors.invoice_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select an invoice'"></option>
                                                            <option v-for="(invoice,index) in invoices"
                                                                    v-html="invoice.reference + ' - £' + number_format(parseFloat(invoice.amount).toFixed(2)) + ' - ' + invoice.invoiced_at"
                                                                    :value="invoice.id"></option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-2">
                                                    <label for="gbp_amount"
                                                           class="block text-sm text-gray-900 required">
                                                        GBP Amount
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="transaction_type_title" v-model="form.amount"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.amount}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="col-span-2 flex justify-end">
                                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving Invoice..</span>
                                                        <span v-else>Add Related Invoice</span>
                                                    </button>
                                                </div>

                                                <h2 class="text-xl text-bold col-span-2">Currently Assigned Invoices</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative col-span-2">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Invoice
                                                            </th>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Amount
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="related_invoices.length > 0" v-for="(related_invoice, index) in related_invoices"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ related_invoice.reference }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                £{{ number_format(parseFloat(related_invoice.pivot.amount).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">

                                                                <a @click="deleteRelatedInvoice(related_invoice)"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                        </tr>

                                                        <tr :class="[(parseFloat(supplierInvoiceTotal) == parseFloat(relatedInvoiceTotal)) ? 'bg-green-100' : 'bg-red-100']">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                Total
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                £{{ number_format(parseFloat(relatedInvoiceTotal).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                                                            </td>
                                                        </tr>
                                                        <tr class="bg-gray-200">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                Invoice Total
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                                £{{ number_format(parseFloat(supplierInvoiceTotal).toFixed(2)) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                                                            </td>
                                                        </tr>
                                                        <tr v-if="related_invoices.length === 0">
                                                            <td colspan="3" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                There are no related invoice for this supplier invoice.
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {
    LinkIcon,
    PlusSmIcon,
    QuestionMarkCircleIcon,
    ExclamationCircleIcon,
    UploadIcon,
    PaperClipIcon,
    DownloadIcon
} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3';
import SlideOutErrors from "@/Components/SlideOutErrors";
import {mapState} from "vuex";
import {emitter} from "@/eventHub";
import moment from "moment";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        UploadIcon,
        PaperClipIcon,
        DownloadIcon
    },
    computed: {
        ...mapState({
            invoices: state => state.supplierinvoice.invoices ?? null
        })
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const updating = false;

        const loading = ref(null);

        const fixture = usePage().props.fixture;

        const relatedInvoiceTotal = ref(0);

        const related_invoices = ref([]);

        const supplierInvoiceTotal = ref(0);

        const form = useForm({
            id: null,
            invoice_id: "",
            supplier_invoice_id: "",
            amount: null
        })

        function submit() {
            if (this.updating) {
                this.form.post(route('frontend.fixture.supplier-invoice.invoice.related-invoice.update', [usePage().props.fixture.id, this.form.supplier_invoice_id, this.form.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('frontend.fixture.supplier-invoice.invoice.related-invoice.store', [usePage().props.fixture.id, this.form.supplier_invoice_id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        }

        function close() {
            this.form.reset();
            this.form.clearErrors();
            this.updating = false;
            this.show = false;
        }

        function open(supplier_invoice) {
            this.$store.dispatch('supplierinvoice/getLinkData', {
                fixture_id: fixture.id,
                supplier_invoice_id: supplier_invoice.id
            });
            if (typeof fixture !== 'undefined') {
                this.form.fixture_id = fixture.id
            }
            this.related_invoices = supplier_invoice.related_invoices
            this.relatedInvoiceTotal = supplier_invoice.total_amount
            this.supplierInvoiceTotal = supplier_invoice.gbp_amount
            this.form.supplier_invoice_id = supplier_invoice.id
            this.form.clearErrors();
            this.show = true;
        }

        const emitter = emitter;

        return {
            show,
            form,
            submit,
            updating,
            close,
            open,
            showErrors,
            emitter,
            loading,
            fixture
        }
    },
    methods: {
        deleteRelatedInvoice(related_invoice) {
            this.$swal.fire({
                title: 'Remove Related Invoice',
                html: "Removing this invoice will remove any associated information to the supplier invoice and cannot be recovered.<br/><br/>" +
                    "Are you sure you wish to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        // Delete the region
                        this.$inertia.delete(route('frontend.fixture.supplier-invoice.invoice.delete-linked-invoice', [this.fixture.id, this.form.supplier_invoice_id, related_invoice.pivot.id]), {
                            preserveScroll: true,
                            onSuccess: (page) => {
                                // Make a request for a regions to be fetched.
                                axios.get(route('frontend.fixture.supplier-invoice.invoice.list', [this.fixture.id, this.form.supplier_invoice_id]))
                                    .then(response => {
                                        this.related_invoices = response.data.related_invoices;
                                        this.relatedInvoiceTotal = response.data.total_amount;
                                    }).finally(() => {
                                    this.loading = false
                                });
                            },
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true;

                            }
                        });
                    }
                });
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
    },
    mounted() {
        emitter.on('openLinkSupplierInvoiceSlideout', (supplier_invoice) => {
            this.open(supplier_invoice);
        });

        emitter.on('editLinkSupplierInvoiceSlideout', (supplier_invoice) => {
            this.updating = true;
            this.form.id = supplier_invoice.id;
            this.open();
        })

    },
    created() {
        this.moment = moment
    }
}
</script>
